import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  EmptyIndicator,
  MoreOptions,
  Pagination,
  ProgressBar,
  Tooltip,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableRow
} from 'components/common/box';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useAppliedFilterParams } from 'components/common/filter';
import {
  CalendarEventFeedbackResponseDelete,
  CalendarEventFeedbackResponsesSubHeader
} from '.';

import type { GetCalendarEventFeedbackSubmissionsRes } from 'shared-types/responses';
import { Icon } from 'components/common/icons';

const filterId = 'calendar-event-feedback-responses';

interface Props {
  updateAnalytics?: () => Promise<void>;
  updateQuestions?: () => Promise<void>;
}

const CalendarEventFeedbackResponses = ({
  updateAnalytics,
  updateQuestions
}: Props) => {
  const {
    messages,
    pageState: { calendarEvent },
    theme
  } = useReduxState();

  const router = useRouter();

  // the page, the search term and the filters all live in the url, so opening
  // one person's answers and coming back lands on the same list
  const page = parseInt(router.query.page as string, 10) || 1;
  const search = (router.query.search as string) || '';
  const appliedFilters = useAppliedFilterParams(filterId);
  const filterParams = new URLSearchParams(appliedFilters).toString();

  const { data, loading, updateData } =
    useApiData<GetCalendarEventFeedbackSubmissionsRes>({
      url:
        `/admin/calendar-events/${calendarEvent.id}/feedback-form/submissions` +
        `?limit=10&page=${page}` +
        (search ? `&search=${encodeURIComponent(search)}` : '') +
        (filterParams ? `&${filterParams}` : '')
    });

  const submissions = data?.submissions || [];

  const updateQuery = (query: Record<string, number | string>) => {
    router.replace({ query: { ...router.query, ...query } });
  };

  const updateResponses = async () => {
    await updateAnalytics?.();

    // unblock the questions after deleting the last response
    if (data?.numberOfSubmissions === 1) await updateQuestions?.();

    // the deleted response was the only one on this page, so the page itself
    // is gone. step back rather than leave the box on an empty page
    if (submissions.length === 1 && page > 1) updateQuery({ page: page - 1 });
    else await updateData();
  };

  const updateResponsesAfterDeleteAll = async () => {
    if (page === 1) await updateData();
    else updateQuery({ page: 1 });

    await updateAnalytics?.();
    await updateQuestions?.();
  };

  return (
    <div className="responses">
      <Box
        headerContent={
          <MoreOptions category="lighter" styleContentButtons={false}>
            <CalendarEventFeedbackResponseDelete
              disabled={!submissions.length}
              updateResponses={updateResponsesAfterDeleteAll}
            />
          </MoreOptions>
        }
        loading={loading}
        padding={0}
        subHeaderContent={
          <CalendarEventFeedbackResponsesSubHeader filterId={filterId} />
        }
        title={
          <div className="title">
            {messages.eventCalendarFeedbackResponses}

            {data ? (
              <div className="title-progress">
                <div className="title-progress-label">
                  {getMessageWithPlaceholders({
                    message: messages.usersNumberOfUsers,
                    placeholders: {
                      numberOfUsers: `${data.numberOfEligibleUserSubmissions} / ${data.numberOfEligibleUsers}`
                    }
                  })}

                  <Tooltip
                    content={messages.eventCalendarFeedbackResponsesUsersInfo}
                  >
                    <div className="title-progress-label-icon">
                      <span className="sr-only">
                        {messages.eventCalendarFeedbackResponsesUsersInfo}
                      </span>
                      <Icon icon="question-circle" />
                    </div>
                  </Tooltip>
                </div>

                <div className="title-progress-bar">
                  <ProgressBar
                    color={theme.successStandaloneColor}
                    progress={
                      data.numberOfEligibleUsers
                        ? data.numberOfEligibleUserSubmissions /
                          data.numberOfEligibleUsers
                        : 0
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        }
      >
        {submissions.length ? (
          <BoxTable>
            {submissions.map((submission) => (
              <BoxTableRow key={submission.id}>
                <BoxTableCell>
                  <User
                    anonymous={!submission.user}
                    enableProfileLink={true}
                    linkAs={`/calendar/${calendarEvent.id}/feedback/responses/${submission.id}`}
                    linkHref="/calendar/[calendarEventId]/feedback/responses/[responseId]"
                    user={submission.user || undefined}
                  />
                </BoxTableCell>

                <BoxTableCell>
                  {getFormattedDate({ date: submission.createdAt })}
                </BoxTableCell>

                <BoxTableCell>
                  {getMessageWithPlaceholders({
                    message: messages.commonNumberOfAnswers,
                    placeholders: {
                      numberOfAnswers: submission.numberOfAnswers
                    }
                  })}
                </BoxTableCell>

                <BoxTableCell shrink={true}>
                  <MoreOptions styleContentButtons={false}>
                    <CalendarEventFeedbackResponseDelete
                      submissionId={submission.id}
                      updateResponses={updateResponses}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator
            icon="comments"
            title={messages.eventCalendarFeedbackResponsesEmpty}
          />
        )}
      </Box>

      {submissions.length ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => updateQuery({ page })}
        />
      ) : null}

      <style jsx>{`
        .title {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px 50px;
        }
        .title-progress {
          align-items: center;
          display: flex;
          gap: 15px;
          position: relative;
          top: 1px;
          white-space: nowrap;
        }
        .title-progress-label {
          align-items: center;
          color: ${theme.textColorLighter};
          display: flex;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 5px;
        }
        .title-progress-label-icon {
          font-size: 1.2rem;
        }
        .title-progress-bar {
          position: relative;
          top: 1px;
          width: 100px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackResponses;
